import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Camera, Image, Wallet, Key, Settings, FileText,
  Menu, X, LogOut, ChevronDown, User, Bell, Users, Crown, HelpCircle, BarChart3, Gift, Package, TrendingUp, Lock, Code, Store, Link2, Plug, UserCheck, Shield
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import OnboardingWizard from './OnboardingWizard';
import { partnerAPI, subscriptionAPI } from '../api';

const PartnerLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingKey, setOnboardingKey] = useState(0);
  const [subscription, setSubscription] = useState(null);
  const [platformBillingEnabled, setPlatformBillingEnabled] = useState(false);

  const checkOnboarding = async () => {
    try {
      const response = await partnerAPI.getOnboarding();
      setShowOnboarding(response.data.show_onboarding);
    } catch (error) {
      console.error('Failed to check onboarding:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await subscriptionAPI.getCurrent();
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  useEffect(() => {
    checkOnboarding();
    fetchSubscription();
  }, [location.pathname]);

  // Fetch platform-level feature flags once on mount. Controls whether
  // Nanokart's own billing surface (Usage & Billing + Subscription tabs)
  // is visible; disabled when partners are on an external billing arrangement.
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform/config`)
      .then((r) => r.json())
      .then((cfg) => { if (!cancelled) setPlatformBillingEnabled(!!cfg.platform_billing_enabled); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleOnboardingDismiss = () => {
    setShowOnboarding(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/partners/login');
  };

  // Determine feature access based on subscription
  const hasInventory = subscription?.has_inventory || true;  // All plans now have inventory access
  const currentPlan = subscription?.current_plan || 'starter';

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/partners' },
    { icon: Store, label: 'In-Store', path: '/partners/in-store' },
    { icon: Link2, label: 'Social', path: '/partners/social' },
    { icon: Plug, label: 'Integrations', path: '/partners/integrations' },
    { icon: UserCheck, label: 'Customers', path: '/partners/customers' },
    { icon: Package, label: 'Inventory', path: '/partners/inventory' },
    { icon: Shield, label: 'SmartGate', path: '/partners/smartgate', badge: 'PRO', proOnly: true },
    // "Usage & Billing" and "Subscription" are only shown when Nanokart's own
    // billing surface is enabled. Hidden by default so partners on external
    // billing arrangements don't see an inapplicable UI (also required by
    // some app-store reviews when payments are handled off-platform).
    ...(platformBillingEnabled
      ? [{ icon: Wallet, label: 'Usage & Billing', path: '/partners/wallet' }]
      : []),
    { icon: Image, label: 'Gallery', path: '/partners/gallery' },
    { icon: BarChart3, label: 'Analytics', path: '/partners/analytics' },
    { icon: Gift, label: 'Referral', path: '/partners/referral' },
    { icon: Code, label: 'Developers', path: '/partners/api-keys' },
    { icon: Users, label: 'Team', path: '/partners/team' },
    ...(platformBillingEnabled
      ? [{ icon: Crown, label: 'Subscription', path: '/partners/subscription' }]
      : []),
    { icon: HelpCircle, label: 'Help Center', path: '/partners/help' },
    { icon: Settings, label: 'Settings', path: '/partners/settings' },
    { icon: FileText, label: 'Audit Logs', path: '/partners/audit-logs' },
  ];

  // Team members (sub-admins & users) get a restricted navigation
  const teamRole = user?.team_role || null;
  const memberHiddenPaths = [
    '/partners/wallet', '/partners/referral', '/partners/api-keys',
    '/partners/subscription', '/partners/settings', '/partners/audit-logs',
  ];
  const visibleNavItems = teamRole
    ? navItems.filter((item) =>
        !memberHiddenPaths.includes(item.path) &&
        !(teamRole === 'user' && item.path === '/partners/team'))
    : navItems;

  return (
    <div className="min-h-screen bg-slate-50 flex overflow-x-hidden">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:block ${sidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-slate-200 transition-all duration-300 flex-shrink-0`}>
        <div className="h-full flex flex-col">
          {/* Logo - Links to Homepage */}
          <Link to="/" className="p-4 border-b border-slate-100 block hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-3">
              {sidebarOpen ? (
                <img 
                  src="/new-logo.png" 
                  alt="Nanokart.ai" 
                  className="h-20 w-auto"
                />
              ) : (
                <img 
                  src="/logo-icon.png" 
                  alt="N" 
                  className="w-12 h-12 rounded-lg object-contain"
                />
              )}
            </div>
          </Link>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1">
            {visibleNavItems.map((item) => (
              item.locked ? (
                <div
                  key={item.path}
                  onClick={() => navigate('/partners/subscription')}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm cursor-pointer text-slate-400 hover:bg-slate-50 group"
                  title={`Upgrade to ${item.requiredPlan} to unlock`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && (
                    <span className="flex items-center gap-2 flex-1">
                      {item.label}
                      <Lock className="w-3.5 h-3.5 text-slate-400 group-hover:text-amber-500" />
                    </span>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/partners'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${isActive 
                      ? 'bg-blue-50 text-blue-700 font-medium' 
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`
                  }
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && (
                    <span className="flex items-center gap-2">
                      {item.label}
                      {item.badge && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-amber-400 to-amber-500 text-white rounded">
                          {item.badge}
                        </span>
                      )}
                    </span>
                  )}
                </NavLink>
              )
            ))}
          </nav>

          {/* Toggle */}
          <div className="p-3 border-t border-slate-100">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-slate-500 hover:text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm"
            >
              <Menu className="w-4 h-4" />
              {sidebarOpen && <span>Collapse</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Top Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
          <div className="flex items-center justify-between h-14 px-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 text-slate-500 hover:text-slate-700"
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex-1" />

            {/* Right side */}
            <div className="flex items-center gap-3">
              <button className="p-2 text-slate-400 hover:text-slate-600 relative">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-blue-600 rounded-full" />
              </button>

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-slate-900" data-testid="header-user-name">
                      {teamRole ? (user?.team_user_name || user?.contact_name || 'Team Member') : (user?.business_name || 'Partner')}
                    </p>
                    {teamRole && (
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-600" data-testid="header-team-role">
                        {teamRole === 'sub_admin' ? 'Sub-Admin' : 'User'}
                      </p>
                    )}
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute right-0 mt-2 w-48 bg-white rounded-lg py-1 shadow-lg border border-slate-200"
                    >
                      {!teamRole && (
                        <NavLink
                          to="/partners/settings"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                        >
                          <Settings className="w-4 h-4" />
                          Settings
                        </NavLink>
                      )}
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-3 sm:p-6 overflow-x-hidden w-full">
          <Outlet />
        </main>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/40 z-50 lg:hidden"
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              className="fixed left-0 top-0 bottom-0 w-64 bg-white z-50 lg:hidden shadow-xl"
            >
              <div className="h-full flex flex-col">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                    <img 
                      src="/new-logo.png" 
                      alt="Nanokart.ai" 
                      className="h-20 w-auto"
                    />
                  </Link>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 text-slate-500 hover:text-slate-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <nav className="flex-1 p-3 space-y-1">
                  {visibleNavItems.map((item) => (
                    item.locked ? (
                      <div
                        key={item.path}
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate('/partners/subscription');
                        }}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm cursor-pointer text-slate-400 hover:bg-slate-50"
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="flex items-center gap-2 flex-1">
                          {item.label}
                          <Lock className="w-3.5 h-3.5 text-amber-500" />
                        </span>
                      </div>
                    ) : (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.path === '/partners'}
                        onClick={() => setMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${isActive 
                            ? 'bg-blue-50 text-blue-700 font-medium' 
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`
                        }
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="flex items-center gap-2">
                          {item.label}
                          {item.badge && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-amber-400 to-amber-500 text-white rounded">
                              {item.badge}
                            </span>
                          )}
                        </span>
                      </NavLink>
                    )
                  ))}
                </nav>

                <div className="p-3 border-t border-slate-100">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-red-600 rounded-lg hover:bg-red-50 text-sm"
                  >
                    <LogOut className="w-5 h-5" />
                    Sign Out
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Onboarding Wizard */}
      {showOnboarding && (
        <OnboardingWizard 
          key={onboardingKey}
          onDismiss={handleOnboardingDismiss}
        />
      )}
    </div>
  );
};

export default PartnerLayout;
